import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../../state'
import * as createEmployeeForm from '../create-employee-form'

const DEPT_ENTITY_ID =
  ents.extra.quiver.resources.data.dimensions.organization.entities.department
    .MODEL_ID
/** Gap width between selected employee tag items in pixels */
const SELECTED_EMPLOYEE_ITEM_GAP_WIDTH = 8

/** Width reserved for the expand/collapse button in pixels */
const SELECTED_EMPLOYEE_EXPAND_BUTTON_WIDTH = 40

/** Shared employee selector for space modals — renders name/department filters, selected tags, and an employee checkbox list */
@ngCore.Component({
  imports: [
    createEmployeeForm.Component,
    ds.input.checkbox.Component,
    ds.input.select.multiple.Component,
    ds.input.text.Component,
    ds.loader.Component,
    ds.tag.Component,
    ngCommon.NgClass,
  ],
  selector: 'atn-space-modal-employee-selector',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** Available employees to display in the list */
  public readonly employeesSg = ngCore.input.required<
    ents.extra.space.Employee[]
  >({ alias: 'employees' })

  /** Currently selected employees, shown as dismissible tags */
  public readonly selectedEmployeesSg = ngCore.input.required<
    ents.extra.space.Employee[]
  >({ alias: 'selectedEmployees' })

  /** @see {@link state.space.full.employee.Collection} */
  public readonly employeesEc = ngCore.inject(
    state.space.full.employee.Collection,
  )

  /** @see {@link state.space.full.jobTitles.Collection} */
  public readonly jobTitlesEc = ngCore.inject(
    state.space.full.jobTitles.Collection,
  )

  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /**
   * Department options for the department filter select, derived from all
   * departments in the organizations collection.
   */
  public readonly departmentOptionsSg = ngCore.computed(
    (
      allDeptEc = this._organizationsFeature.selectors.allDepartmentEc.asSignal(),
    ) =>
      allDeptEc?.plugins.cachePlugin.feature.selectors
        .getCachedEntities()
        .asSignal()
        .filter(TypeChecker.isNotNullish.raw)
        .map(org => ({ label: org.name, value: org.id })) ?? [],
  )

  /** Current organization filter record IDs */
  public readonly orgFilterSg = ngCore.computed(
    (
      filters = this.employeesEc.plugins.currentListPlugin.appliedParams.selectors.filters.asSignal(),
    ) => filters?.orgFilter,
  )

  /** Emitted when an employee checkbox is toggled */
  public readonly employeeToggled = ngCore.output<{
    id: ents.extra.space.Employee.Id
    selected: boolean
  }>()

  /** Emitted when a selected employee tag close button is clicked */
  public readonly employeeRemoved =
    ngCore.output<ents.extra.space.Employee.Id>()

  /** References to selected item elements, used for overflow detection */
  public readonly selectedItemsRefSg =
    ngCore.viewChildren<ngCore.ElementRef<HTMLElement>>('selectedItemRef')

  /** Reference to the selected items container, used for overflow detection */
  public readonly selectItemsContainerRefSg = ngCore.viewChild<
    ngCore.ElementRef<HTMLElement>
  >('selectItemsContainerRef')

  /** Whether the selected items list is expanded */
  public readonly expandedSg = ngCore.signal(false)

  /**
   * Whether to show the expand/collapse button.
   * True when selected items overflow their container.
   */
  public readonly showExpandButtonSg = ngCore.computed(() => {
    const selectedItemsRef = this.selectedItemsRefSg()
    const selectItemsContainerRef = this.selectItemsContainerRefSg()

    if (selectedItemsRef.length === 0) {
      return false
    }

    const childrenWidth = selectedItemsRef.reduce(
      (prev, curr) => prev + (curr?.nativeElement?.offsetWidth ?? 0),
      0,
    )

    const offsetWidth = selectItemsContainerRef?.nativeElement?.offsetWidth ?? 0

    // Add gaps between items and space for the expand/collapse button
    const totalWidth =
      childrenWidth +
      (selectedItemsRef.length - 1) * SELECTED_EMPLOYEE_ITEM_GAP_WIDTH +
      SELECTED_EMPLOYEE_EXPAND_BUTTON_WIDTH

    return totalWidth > offsetWidth && offsetWidth > 0
  })

  /** Handles name search input changes */
  public onNameSearchChange = (name: string | undefined): void => {
    this.employeesEc.plugins.currentListPlugin.appliedParams.actions.setSearch
      .from('EmployeeSelector.Filters.Changed')
      .dispatch(name)
  }

  /** Handles department filter changes */
  public onDepartmentChange = (
    departmentIds: readonly ds.input.select.models.Item<ents.extra.space.Organization.Id>['value'][],
  ): void => {
    const filters =
      this.employeesEc.plugins.currentListPlugin.appliedParams.selectors.filters.asSignal()
    this.employeesEc.plugins.currentListPlugin.appliedParams.actions.setFilters
      .from('EmployeeSelector.Filters.Changed')
      .dispatch({
        ...filters,
        orgFilter: departmentIds?.length
          ? {
              entityId: DEPT_ENTITY_ID,
              recordIds: departmentIds.filter(TypeChecker.isNotNullish.raw),
            }
          : undefined,
      })
  }

  /**
   * Checks if a given employee is currently selected.
   */
  public isEmployeeSelected = (id: ents.extra.space.Employee.Id): boolean =>
    this.selectedEmployeesSg().some(e => e.id === id)

  /** Toggles the expanded state of the selected items list */
  public toggleExpandButtonClickHandler = (): void => {
    this.expandedSg.update(v => !v)
  }

  /** Opens the create-employee form dialog */
  public createEmployeeClickHandler = (): void => {
    const { entityId, recordIds: [recordId] = [] } = this.orgFilterSg() ?? {}
    if (entityId && recordId) {
      this.employeesEc.plugins.editingPlugin.feature.actions.edit
        .from('EmployeeSelector.CreateEmployee.Clicked')
        .dispatch({
          edit: {
            input: {
              organization: {
                entityId,
                recordId,
              },
            },
          },
        })
    }
  }

  /**
   * Returns the checkbox label for an employee, appending their job title name
   * if it is present in the lookup.
   */
  public getEmployeeLabel(
    employee: ents.extra.space.Employee,
    jobTitleLookup: Partial<
      Record<ents.extra.space.JobTitle.Id, ents.extra.space.JobTitle>
    >,
  ): string {
    const jobTitle = employee.jobTitleId && jobTitleLookup[employee.jobTitleId]
    if (!jobTitle) {
      return employee.name
    }
    return `${employee.name}, ${jobTitle.name}`
  }
}
